import { APIError, APIResource, APIResponse } from "../utils/APIResource";
import config from "../config/config";
import CiamResponseCode from "../utils/CiamResponseCode.js";

export default class UserService {
  static login(params) {
    let grant_type = "password";

    return APIResource.post(
      `oauth/token`,
      `grant_type=${grant_type}&username=${params.username}&password=${params.password}`,
      {
        headers: {
          Authorization: `Basic ${config.api.authBearer}`,
          "Content-Type": "application/x-www-form-urlencoded"
        }
      }
    )
      .then(resp => {
        return new APIResponse(resp);
      })
      .catch(error => {
        return new APIError(error);
      });
  }

  static getJwtInfo(jwtAdditionalInfoUUID) {
    return APIResource.get(`v1/jwt/info/${jwtAdditionalInfoUUID}`)
      .then(resp => Promise.resolve(new APIResponse(resp)))
      .catch(error => {
        return Promise.reject(new APIError(error));
      });
  }

  static logout() {
    return APIResource.post("/v1/logout")
      .then(resp => {
        return Promise.resolve(new APIResponse(resp));
      })
      .catch(error => {
        return Promise.reject(new APIError(error));
      });
  }

  static me() {
    return APIResource.get("/v1/enrolment/employee/me").then(
      response => {
        return Promise.resolve(new APIResponse(response));
      },
      error => {
        return Promise.reject(new APIError(error));
      }
    );
  }
  static policy() {
    return APIResource.get("/v1/employee/policy").then(
      response => {
        return Promise.resolve(new APIResponse(response));
      },
      error => {
        return Promise.reject(new APIError(error));
      }
    );
  }

  static getEmployee(personId) {
    return APIResource.get(`/v1/enrolment/employee/${personId}/detail`).then(
      response => {
        return Promise.resolve(new APIResponse(response));
      },
      error => {
        return Promise.reject(new APIError(error));
      }
    );
  }

  static forgotPassword(params) {
    return APIResource.post(
      `/v1/open/enrolment/pwd`,
      {
        username: params.username
      },
      {}
    )
      .then(resp => {
        return Promise.resolve(new APIResponse(resp));
      })
      .catch(error => {
        return Promise.reject(new APIError(error));
      });
  }

  static register(params) {
    return APIResource.post(
      `/v1/open/enrolment/registration`,
      {
        employee_no: params.employeeNo,
        policy_no: params.policyNo,
        dob: params.dob,
        email: params.email
      },
      {}
    )
      .then(resp => {
        return Promise.resolve(new APIResponse(resp));
      })
      .catch(error => {
        return Promise.reject(new APIError(error));
      });
  }

  static async resendOtp(code = "login") {
    try {
      const resp = await APIResource.post(`/v1/open/resendOtp`, { code });
      return new APIResponse(resp);
    } catch (error) {
      console.error(
        `An error occurred while requesting OTP: ${error.message}`,
        error
      );
      throw new APIError(error);
    }
  }

  static CIAMLogin(params) {
    let grant_type = "password";
    return APIResource.post(
      `oauth/token`,
      `grant_type=${grant_type}&username=${params.username}&password=${params.password}`,
      {
        headers: {
          Authorization: `Basic ${config.api.authBearer}`,
          "x-auth-type": "CIAM",
          "x-auth-random-key": params.randomKey,
          "x-auth-tx-id": params.txnId,
          "x-auth-app": config.app.type,
          "x-auth-cnonce": params.cnonce,
          "x-auth-cnonce-hash": params.chash
        }
      }
    )
      .then(resp => {
        return Promise.resolve(new APIResponse(resp));
      })
      .catch(error => {
        return Promise.reject(new APIError(error));
      });
  }

  static getRSA(params) {
    return APIResource.get("/v1/open/auth/rsa", {
      headers: {
        "x-auth-app": config.app.type
      }
    })
      .then(resp => {
        return Promise.resolve(new APIResponse(resp));
      })
      .catch(error => {
        return Promise.reject(new APIError(error));
      });
  }

  static getOtp(params) {
    return APIResource.get("/v1/auth/otp", {
      headers: {
        "x-auth-type": "CIAM",
        "x-auth-random-key": params.randomKey,
        "x-auth-tx-id": params.txnId,
        "x-auth-app": config.app.type
      }
    })
      .then(resp => {
        return Promise.resolve(new APIResponse(resp));
      })
      .catch(error => {
        return Promise.reject(new APIError(error));
      });
  }

  static getResetPasswordOTP(code) {
    return APIResource.post("/v1/open/enrolment/pwd/otp", {
      code: code
    })
      .then(resp => {
        return Promise.resolve(new APIResponse(resp));
      })
      .catch(error => {
        return Promise.reject(new APIError(error));
      });
  }

  static getResetLinkValidity(code) {
    return APIResource.get(`/v1/open/pwd/${code}/status`)
      .then(resp => {
        return Promise.resolve(new APIResponse(resp));
      })
      .catch(error => {
        return Promise.resolve(new APIError(error));
      });
  }

  static putResetPasswordOTP(params) {
    return APIResource.put(
      "/v1/open/enrolment/pwd",
      {
        code: params.code,
        otp: params.otp,
        new_password: params.new_password,
        confirm_password: params.confirm_password
      },
      {
        headers: {
          Authorization: `Basic ${config.api.authBearer}`,
          "x-auth-type": "CIAM",
          "x-auth-random-key": params.randomKey,
          "x-auth-tx-id": params.txnId,
          "x-auth-app": config.app.type
        }
      }
    )
      .then(resp => {
        return Promise.resolve(new APIResponse(resp));
      })
      .catch(error => {
        return Promise.reject(new APIError(error));
      });
  }

  static postOTP(params) {
    return APIResource.post(
      "/v1/auth/otp/",
      {
        otp: params.otp
      },
      {
        headers: {
          "x-auth-type": "CIAM",
          "x-auth-random-key": params.randomKey,
          "x-auth-tx-id": params.txnId,
          "x-auth-app": config.app.type
        }
      }
    )
      .then(resp => {
        return Promise.resolve(new APIResponse(resp));
      })
      .catch(error => {
        return Promise.reject(new APIError(error));
      });
  }

  static refreshToken(params) {
    return APIResource.post("v1/auth/tokens/refresh", {}, {
      headers: {
        "x-auth-type": "CIAM",
        "x-auth-random-key": params["x-auth-random-key"],
        "x-auth-tx-id": params["x-auth-tx-id"],
        "x-auth-app": config.app.type
      }
    })
      .then(resp => Promise.resolve(new APIResponse(resp)))
      .catch(error => {
        return Promise.reject(new APIError(error));
      });
  }

  static revokeToken() {
    return APIResource.delete("v1/auth/tokens/revoke")
      .then(resp => Promise.resolve(new APIResponse(resp)))
      .catch(error => {
        return Promise.reject(new APIError(error));
      });
  }

  static agreeTnc(params) {
    return APIResource.post("/v1/auth/tc", {
      marketing_consent: params.marketingConsent,
      tnc: params.tnc
    })
      .then(resp => {
        return Promise.resolve(new APIResponse(resp));
      })
      .catch(error => {
        return Promise.reject(new APIError(error));
      });
  }

  static postEnrolment(id, payload) {
    return APIResource.post(`/v1/enrolment/employee/${id}`, payload, {})
      .then(resp => {
        return Promise.resolve(new APIResponse(resp));
      })
      .catch(error => {
        return Promise.reject(new APIError(error));
      });
  }

  static saveMe(payload) {
    return APIResource.post("/v1/enrolment/employee/me", payload, {})
      .then(resp => {
        return Promise.resolve(new APIResponse(resp));
      })
      .catch(error => {
        return Promise.reject(new APIError(error));
      });
  }

  static updateMe(payload) {
    return APIResource.put("/v1/enrolment/employee/me", payload, {})
      .then(resp => {
        return Promise.resolve(new APIResponse(resp));
      })
      .catch(error => {
        return Promise.reject(new APIError(error));
      });
  }

  static saveDependent(employeeId, payload) {
    return APIResource.post(
      `/v1/enrolment/employee/${employeeId}/dependent`,
      payload,
      {}
    )
      .then(resp => {
        return Promise.resolve(new APIResponse(resp));
      })
      .catch(error => {
        return Promise.reject(new APIError(error));
      });
  }
  static updateDependent(employeeId, payload) {
    return APIResource.put(
      `/v1/enrolment/employee/${employeeId}/dependent`,
      payload,
      {}
    )
      .then(resp => {
        return Promise.resolve(new APIResponse(resp));
      })
      .catch(error => {
        return Promise.reject(new APIError(error));
      });
  }

  static deleteDependent(employeeId, dependentId) {
    return APIResource.put(
      `/v1/enrolment/employee/${employeeId}/dependent/${dependentId}`
    )
      .then(resp => {
        return Promise.resolve(new APIResponse(resp));
      })
      .catch(error => {
        return Promise.reject(new APIError(error));
      });
  }

  static getOccupationClassList(occupation) {
    return APIResource.get(`/v1/occupation/list`, {
      params: {
        excludeLevel: occupation
      }
    }).then(
      response => {
        return Promise.resolve(new APIResponse(response));
      },
      error => {
        return Promise.reject(new APIError(error));
      }
    );
  }

  static checkHDFRequirementCall(personId, params) {
    return (
      APIResource.post(`/v1/enrolment/${personId}/validateHdf`, params, {})
        // return APIResource.get(`http://localhost:3101/users`, {}, {})
        .then(resp => {
          return Promise.resolve(new APIResponse(resp));
        })
        .catch(error => {
          return Promise.reject(new APIError(error));
        })
    );
  }

  static submitHDF(personId, params) {
    return (
      APIResource.post(`/v1/enrolment/${personId}/sign`, params, {})
        // return APIResource.get(`http://localhost:3102/signingResult`, {}, {})
        .then(resp => {
          return Promise.resolve(new APIResponse(resp));
        })
        .catch(error => {
          return Promise.reject(new APIError(error));
        })
    );
  }

  /**
   *
   * @todo RAHUL update the payload and response
   */
  static getKofaxDocumentStatus(packageId) {
    return (
      APIResource.get(`v1/enrolment/document/${packageId}/kofaxCallbackStatus`)
        // return APIResource.get(`http://localhost:3103/documentStatus`, {})
        .then(resp => {
          return Promise.resolve(new APIResponse(resp));
        })
        .catch(error => {
          return Promise.reject(new APIError(error));
        })
    );
  }

  static getKofaxPackageStatus(packageId) {
    return (
      APIResource.get(`v1/enrolment/document/${packageId}/packageStatus`)
        // return APIResource.get(`http://localhost:3104/packageStatus`, {})
        .then(resp => {
          return Promise.resolve(new APIResponse(resp));
        })
        .catch(error => {
          return Promise.reject(new APIError(error));
        })
    );
  }

  static retrieveSignedDocument(personId, packageId) {
    return (
      APIResource.get(
        `v1/enrolment/${personId}/document/${packageId}/signedDocument`
      )
        // return APIResource.get(`http://localhost:3105/retrieveDocument`, {})
        .then(resp => {
          return Promise.resolve(new APIResponse(resp));
        })
        .catch(error => {
          return Promise.reject(new APIError(error));
        })
    );
  }
}
